import React, { useEffect, useRef, useState } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider, { Settings } from 'react-slick';
import { iChevronUp, iChevronDown } from '@hover/icons';
import { Box, Icon } from '@hover/blueprint';
import { INSURANCE_BETTER_WAY_TO_BUILD_CAROUSEL_DATA } from '../../../../constants';
import InsuranceSliderBackgroundVector from '../../../../assets/svgs/InsuranceSliderBackgroundVector';
import StyledSlider from './StyledSlider';
import StyledWrapper from './StyledWrapper';
import DesktopSlide from './DesktopSlide';

const setting: Settings = {
  dots: true,
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  vertical: true,
  verticalSwiping: true,
};

const DesktopSlider: React.FC = () => {
  const sliderRef = useRef<Slider>(null);
  const [windowHeight, setWindowHeight] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const [activeSlide, setActiveSlide] = useState<number>(0);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const scrollContainer: HTMLElement | null =
      document.getElementById('build-container');
    const onScroll = () => {
      if (sliderRef?.current && scrollContainer) {
        const containerBounding = scrollContainer.getBoundingClientRect();
        const containerHeight = -containerBounding.y;

        if (containerHeight < 0) {
          sliderRef.current.slickGoTo(0);
          return null;
        }
        if (containerHeight >= 0 && containerHeight <= windowHeight) {
          return sliderRef.current.slickGoTo(0);
        }
        if (
          containerHeight >= windowHeight &&
          containerHeight <= windowHeight * 2
        ) {
          return sliderRef.current.slickGoTo(1);
        }
        if (containerHeight > windowHeight * 2) {
          sliderRef.current.slickGoTo(1);
          return null;
        }
      }
      return null;
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const sliderSetting: Settings = {
    ...setting,
    appendDots: (dots: JSX.Element[]) => {
      let newDots = [...dots];
      if (sliderRef.current) {
        newDots = [
          <Icon
            cursor="pointer"
            color="primary.300"
            ml="7px"
            mb={700}
            icon={iChevronUp}
            onClick={() => {
              const activeItem =
                Number(
                  document
                    .getElementsByClassName('slick-active')[0]
                    .getAttribute('data-index'),
                ) + 1;
              if (activeItem > 1 && sliderRef?.current) {
                window?.scrollTo({ top: windowHeight * activeItem });
                sliderRef.current?.slickPrev();
              }
            }}
            key="prevIcon"
          />,
          ...dots,
          <Icon
            color="primary.300"
            cursor="pointer"
            ml="7px"
            mt={600}
            icon={iChevronDown}
            onClick={() => {
              const activeItem =
                Number(
                  document
                    .getElementsByClassName('slick-active')[0]
                    .getAttribute('data-index'),
                ) + 1;
              if (activeItem < 2 && sliderRef?.current) {
                window?.scrollTo({ top: windowHeight * (activeItem + 1) });
                sliderRef.current?.slickNext();
              }
            }}
            key="nextIcon"
          />,
        ];
      }
      return <ul> {newDots} </ul>;
    },
    beforeChange: (_, next) => setActiveSlide(next),
  };

  return (
    <StyledWrapper
      maxW="5xl"
      overflow="hidden"
      d={{ base: 'none', desktop: 'flex' }}
      id="scroll-element"
      alignItems="center"
      justifyContent="center"
      postition="relative"
    >
      <Box d="block" className="svg-container">
        <Box
          d="flex !important"
          alignItems="center"
          justifyContent="space-between"
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          mx={400}
        >
          <Box mt="50px" className="svg-image-container" aria-hidden>
            <InsuranceSliderBackgroundVector />
          </Box>
          <Box ml={1000} flex={1} justifyContent="center" flexDir="column" />
        </Box>
        <Box
          d="flex !important"
          alignItems="center"
          justifyContent="space-between"
          position="absolute"
          top="52px"
          left="0"
          right="0"
          bottom="0"
          mx={400}
          zIndex={10}
        >
          <Box maxW="381px" maxH="560px" className="mobile-frame" aria-hidden>
            <StaticImage
              placeholder="blurred"
              alt="phone"
              objectFit="contain"
              src="../../../../assets/images/device-frame.png"
            />
          </Box>
          <Box ml={1000} flex={1} justifyContent="center" flexDir="column" />
        </Box>
        <Box d="block" aria-live="polite">
          <StyledSlider ref={sliderRef} {...sliderSetting}>
            {INSURANCE_BETTER_WAY_TO_BUILD_CAROUSEL_DATA.map(
              ({ index, img, body, icon }) => (
                <DesktopSlide
                  activeSlide={activeSlide}
                  key={index}
                  id={index}
                  icon={icon}
                  body={body}
                  aria-hidden={activeSlide !== index}
                  img={img}
                />
              ),
            )}
          </StyledSlider>
        </Box>
      </Box>
    </StyledWrapper>
  );
};

export default DesktopSlider;
